import React, { useState, useEffect, useCallback } from 'react';
import {
    getSupportTickets,
    getSupportTicket,
    replySupportTicket,
    updateSupportTicket,
} from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { MessageSquare, RefreshCw, CheckCircle, RotateCcw, Search } from 'lucide-react';
import './SupportAdminPanel.css';

// --- Type declarations matching the api.ts signatures ---

interface SupportTicket {
    ticket_id: string;
    short_id?: string;
    user_id: string;
    user_email?: string;
    user_name?: string;
    subject: string;
    category: string;
    priority: string;
    status: string;
    created_at: string;
    updated_at: string;
    closed_at?: string;
    message_count?: number;
}

interface SupportTicketMessage {
    message_id: string;
    ticket_id: string;
    author_id: string;
    author_name?: string;
    author_email?: string;
    body: string;
    is_staff_reply: boolean;
    created_at: string;
}

interface SupportTicketDetail extends SupportTicket {
    messages: SupportTicketMessage[];
}

// --- Helpers ---

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const statusBadge = (status: string) => {
    const styles: Record<string, React.CSSProperties> = {
        open: {
            color: 'var(--primary-600)',
            background: 'var(--primary-100)',
            borderColor: 'var(--primary-300)',
        },
        closed: {
            color: 'var(--text-muted)',
            background: 'var(--panel-strong)',
            borderColor: 'var(--stroke)',
        },
    };
    return (
        <span
            style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-semibold)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                border: '1px solid',
                textTransform: 'capitalize',
                ...(styles[status] ?? styles['closed']),
            }}
        >
            {status}
        </span>
    );
};

const priorityBadge = (priority: string) => {
    const styles: Record<string, React.CSSProperties> = {
        urgent: {
            color: 'var(--danger)',
            background: 'rgba(239,68,68,0.08)',
            borderColor: 'rgba(239,68,68,0.3)',
        },
        high: {
            color: '#f97316',
            background: 'rgba(249,115,22,0.08)',
            borderColor: 'rgba(249,115,22,0.3)',
        },
        medium: {
            color: 'var(--primary-500)',
            background: 'var(--primary-100)',
            borderColor: 'var(--primary-300)',
        },
        low: {
            color: 'var(--text-muted)',
            background: 'var(--panel-strong)',
            borderColor: 'var(--stroke)',
        },
    };
    return (
        <span
            style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-semibold)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                border: '1px solid',
                textTransform: 'capitalize',
                ...(styles[priority] ?? {}),
            }}
        >
            {priority}
        </span>
    );
};

// --- Main component ---

const SupportAdminPanel: React.FC = () => {
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedTicket, setSelectedTicket] = useState<SupportTicketDetail | null>(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [replyBody, setReplyBody] = useState('');
    const [replying, setReplying] = useState(false);
    const [replyError, setReplyError] = useState('');
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [listError, setListError] = useState('');

    const loadTickets = useCallback(async () => {
        setLoading(true);
        setListError('');
        try {
            const data = await getSupportTickets({
                status: statusFilter || undefined,
                q: debouncedQuery.trim() || undefined,
            });
            setTickets(data);
        } catch (err: any) {
            setListError('Failed to load support tickets.');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, debouncedQuery]);

    // Debounce the search input so we don't fire on every keystroke
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(searchQuery), 250);
        return () => clearTimeout(t);
    }, [searchQuery]);

    const loadTicketDetail = useCallback(async (id: string) => {
        setDetailLoading(true);
        try {
            const data = await getSupportTicket(id);
            setSelectedTicket(data);
        } catch (err: any) {
            setSelectedTicket(null);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    useEffect(() => {
        if (selectedId) {
            loadTicketDetail(selectedId);
        } else {
            setSelectedTicket(null);
        }
    }, [selectedId, loadTicketDetail]);

    const handleSelectTicket = (id: string) => {
        setSelectedId(id === selectedId ? null : id);
        setReplyBody('');
        setReplyError('');
    };

    const handleReply = async () => {
        if (!selectedId || !replyBody.trim()) return;
        setReplying(true);
        setReplyError('');
        try {
            await replySupportTicket(selectedId, replyBody.trim());
            setReplyBody('');
            await loadTicketDetail(selectedId);
        } catch (err: any) {
            setReplyError('Failed to send reply. Please try again.');
        } finally {
            setReplying(false);
        }
    };

    const handleStatusToggle = async () => {
        if (!selectedTicket) return;
        const newStatus = selectedTicket.status === 'open' ? 'closed' : 'open';
        setStatusUpdating(true);
        try {
            await updateSupportTicket(selectedTicket.ticket_id, { status: newStatus });
            await Promise.all([
                loadTicketDetail(selectedTicket.ticket_id),
                loadTickets(),
            ]);
        } catch (err: any) {
            // silently fail — the detail refresh will restore state
        } finally {
            setStatusUpdating(false);
        }
    };

    // Client-side priority filter
    const filteredTickets = priorityFilter
        ? tickets.filter((t) => t.priority === priorityFilter)
        : tickets;

    const isClosed = selectedTicket?.status === 'closed';

    return (
        <div className="support-admin">
            {/* ── Left pane: ticket queue ── */}
            <div className="support-admin-queue">
                <div className="support-admin-queue-header">
                    <h2 className="support-admin-queue-title">Support Tickets</h2>
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={loadTickets}
                        icon={<RefreshCw size={14} />}
                        title="Refresh"
                    />
                </div>

                <div className="support-admin-search">
                    <Search size={14} className="support-admin-search-icon" aria-hidden />
                    <input
                        type="text"
                        className="support-admin-search-input"
                        placeholder="Search by name, email, or ticket ID (e.g. AB12CD34)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            className="support-admin-search-clear"
                            onClick={() => setSearchQuery('')}
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="support-admin-filters">
                    <select
                        className="support-admin-filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                    </select>

                    <select
                        className="support-admin-filter-select"
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                    >
                        <option value="">All Priorities</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                    </select>
                </div>

                <span className="support-count-label">
                    {loading ? 'Loading…' : `Showing ${filteredTickets.length} ticket${filteredTickets.length !== 1 ? 's' : ''}`}
                </span>

                {listError && (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>{listError}</span>
                )}

                <div className="support-ticket-list">
                    {!loading && filteredTickets.length === 0 && (
                        <div className="support-admin-empty">
                            <MessageSquare size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                            <div>No tickets found</div>
                        </div>
                    )}

                    {filteredTickets.map((ticket) => (
                        <div
                            key={ticket.ticket_id}
                            className={`support-ticket-item${selectedId === ticket.ticket_id ? ' active' : ''}`}
                            onClick={() => handleSelectTicket(ticket.ticket_id)}
                        >
                            <div className="support-ticket-subject" title={ticket.subject}>
                                {ticket.short_id && (
                                    <span className="support-ticket-shortid">#{ticket.short_id}</span>
                                )}
                                {ticket.subject}
                            </div>
                            <div className="support-ticket-row" style={{ marginBottom: 4 }}>
                                {statusBadge(ticket.status)}
                                {priorityBadge(ticket.priority)}
                            </div>
                            <div className="support-ticket-row">
                                <span className="support-ticket-email">
                                    {ticket.user_name || ticket.user_email || ticket.user_id}
                                </span>
                                <span className="support-ticket-email">·</span>
                                <span className="support-ticket-email">
                                    {formatDate(ticket.created_at)}
                                </span>
                                {ticket.message_count !== undefined && (
                                    <>
                                        <span className="support-ticket-email">·</span>
                                        <span className="support-ticket-email">
                                            {ticket.message_count} msg{ticket.message_count !== 1 ? 's' : ''}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Right pane: thread ── */}
            {selectedId && (
                <div className="support-admin-thread">
                    {detailLoading && (
                        <div className="support-admin-loading">Loading ticket…</div>
                    )}

                    {!detailLoading && selectedTicket && (
                        <GlassCard
                            variant="default"
                            padding="lg"
                            className="support-thread-card"
                            style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                        >
                            {/* Thread header */}
                            <div className="support-thread-header">
                                <div className="support-thread-header-row">
                                    <h3 className="support-thread-title">
                                        {selectedTicket.short_id && (
                                            <span className="support-ticket-shortid">#{selectedTicket.short_id}</span>
                                        )}
                                        {selectedTicket.subject}
                                    </h3>
                                    {statusBadge(selectedTicket.status)}
                                    {priorityBadge(selectedTicket.priority)}
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        loading={statusUpdating}
                                        onClick={handleStatusToggle}
                                        icon={isClosed ? <RotateCcw size={14} /> : <CheckCircle size={14} />}
                                    >
                                        {isClosed ? 'Reopen Ticket' : 'Close Ticket'}
                                    </GlassButton>
                                </div>
                                <div className="support-thread-header-row">
                                    <span className="support-thread-meta">
                                        From: <strong>{selectedTicket.user_name || selectedTicket.user_email || selectedTicket.user_id}</strong>
                                    </span>
                                    <span className="support-thread-meta">·</span>
                                    <span className="support-thread-meta">
                                        Opened: {formatDate(selectedTicket.created_at)}
                                    </span>
                                    {selectedTicket.category && (
                                        <>
                                            <span className="support-thread-meta">·</span>
                                            <span className="support-thread-meta" style={{ textTransform: 'capitalize' }}>
                                                {selectedTicket.category}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Message thread */}
                            <div className="support-thread-messages">
                                {selectedTicket.messages.length === 0 && (
                                    <div className="support-admin-empty">No messages yet.</div>
                                )}
                                {selectedTicket.messages.map((msg) => (
                                    <div
                                        key={msg.message_id}
                                        className={`support-message ${msg.is_staff_reply ? 'staff' : 'user'}`}
                                    >
                                        <div className="support-message-meta">
                                            {msg.is_staff_reply
                                                ? 'AiMe Support'
                                                : (msg.author_name || msg.author_email || 'User')
                                            }
                                            {' · '}
                                            {formatDate(msg.created_at)}
                                        </div>
                                        <div className="support-message-bubble">
                                            <p className="support-message-body">{msg.body}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Reply form */}
                            <div className="support-reply-form">
                                {isClosed ? (
                                    <p className="support-closed-note">
                                        Ticket closed — reopen to reply.
                                    </p>
                                ) : (
                                    <>
                                        <span className="support-reply-label">Staff Reply</span>
                                        <textarea
                                            className="support-reply-textarea"
                                            placeholder="Type your reply…"
                                            value={replyBody}
                                            onChange={(e) => setReplyBody(e.target.value)}
                                            disabled={replying}
                                        />
                                        <div className="support-reply-actions">
                                            {replyError ? (
                                                <span className="support-reply-error">{replyError}</span>
                                            ) : (
                                                <span />
                                            )}
                                            <GlassButton
                                                variant="primary"
                                                size="sm"
                                                loading={replying}
                                                disabled={!replyBody.trim()}
                                                onClick={handleReply}
                                                icon={<MessageSquare size={14} />}
                                            >
                                                Send Reply
                                            </GlassButton>
                                        </div>
                                    </>
                                )}
                            </div>
                        </GlassCard>
                    )}
                </div>
            )}

            {/* Placeholder when no ticket selected */}
            {!selectedId && (
                <div className="support-admin-placeholder">
                    Select a ticket to view the thread
                </div>
            )}
        </div>
    );
};

export default SupportAdminPanel;
